import type { ZodType } from 'zod/v4';

// MINT-OVERRIDE: import the mint-extended ClientOptions (with typed nested
// `request?: {...}` / `response?: {...}` fields) rather than upstream's, so
// the namespaced merge below is type-safe instead of erroring on `unknown`
// spreads. Everything else is still from upstream.
import type { ClientOptions } from '../types.js';
import type {
  AbstractRequestConfig,
  EndpointOptions,
  HttpMethod,
  StreamOptions,
} from '../types/index.js';

/**
 * Request type for config creation.
 */
export interface ConfigRequest {
  urlParams?: Record<string, string | number>;
  params?: Record<string, unknown>;
  data?: unknown;
  signal?: AbortSignal | null;
  headers?: Record<string, string>;
  [key: string]: unknown;
}

function parseWithSchema(schema: ZodType | undefined, value: unknown): unknown {
  return schema ? schema.parse(value) : value;
}

/**
 * Creates a request configuration object for the HTTP client.
 *
 * This function:
 * - Validates and parses query parameters using `querySchema` (if provided)
 * - Validates and parses request body data using `requestSchema` (if provided)
 * - Converts request data to FormData if `isMultipart` is true
 * - Merges all request properties (headers, signal, etc.) into the final config
 * - Merges clientOptions into the final config for per-endpoint customization
 *
 * @param request - The request parameters object
 * @param options - Endpoint configuration containing schemas
 * @param method - HTTP method to use
 * @param finalUrlPart - The final URL with parameters bound
 * @param isMultipart - Whether to convert the request data to FormData
 * @returns A request configuration object compatible with the Client interface
 *
 * @internal
 */
export function makeConfig<Options extends EndpointOptions | StreamOptions>(
  request: ConfigRequest,
  options: Options,
  method: HttpMethod,
  finalUrlPart: string,
  isMultipart = false,
) {
  const endpointClientOptions = (
    options as Options & { clientOptions?: ClientOptions }
  ).clientOptions;
  const requestClientOptions = request.clientOptions as
    | ClientOptions
    | undefined;

  // Merge endpoint- and request-level clientOptions. Request-level wins at the
  // top level and for the nested `request` / `response` namespaces so callers
  // can override a per-endpoint default at the call site.
  const clientOptions: ClientOptions | undefined =
    endpointClientOptions || requestClientOptions
      ? {
          ...endpointClientOptions,
          ...requestClientOptions,
          ...(endpointClientOptions?.request || requestClientOptions?.request
            ? {
                request: {
                  ...endpointClientOptions?.request,
                  ...requestClientOptions?.request,
                },
              }
            : {}),
          ...(endpointClientOptions?.response || requestClientOptions?.response
            ? {
                response: {
                  ...endpointClientOptions?.response,
                  ...requestClientOptions?.response,
                },
              }
            : {}),
        }
      : undefined;

  const mergedHeaders = {
    ...endpointClientOptions?.headers,
    ...requestClientOptions?.headers,
    ...request.headers,
  };

  // Forward axios-known keys (timeout) to the top level so axios honors them.
  // Everything else stays nested under `clientOptions` so it can't collide
  // with axios's own config keys (e.g. `response`, `transformRequest`).
  const timeout = clientOptions?.timeout;

  return {
    ...request,
    params: parseWithSchema(
      options.querySchema as ZodType | undefined,
      request.params ?? {},
    ) as Record<string, unknown>,
    method,
    url: finalUrlPart,
    data: isMultipart
      ? makeFormData(request, options)
      : parseWithSchema(
          options.requestSchema as ZodType | undefined,
          request.data,
        ),
    ...(timeout !== undefined ? { timeout } : {}),
    ...(Object.keys(mergedHeaders).length > 0
      ? { headers: mergedHeaders }
      : {}),
    ...(clientOptions ? { clientOptions } : {}),
  } satisfies AbstractRequestConfig;
}

/**
 * Serializes a value for inclusion in FormData.
 *
 * Handles various types:
 * - File instances are returned as-is
 * - null/undefined become empty strings
 * - Dates are converted to ISO strings
 * - Objects with toISOString or toJSON methods are serialized appropriately
 * - Other objects are JSON stringified
 * - Primitives are converted to strings
 *
 * @param value - The value to serialize
 * @returns A string representation or File instance
 * @internal
 */
function serializeFormDataValue(value: unknown): string | File {
  if (value instanceof File) {
    return value;
  }
  if (value == null) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (
    typeof value === 'bigint' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value);
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === 'object') {
    if ('toISOString' in value && typeof value.toISOString === 'function') {
      return value.toISOString();
    }
    // For toJSON:
    if ('toJSON' in value && typeof value.toJSON === 'function') {
      const jsonValue = value.toJSON();
      return typeof jsonValue === 'string'
        ? jsonValue
        : JSON.stringify(jsonValue);
    }
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * Converts request data to FormData for multipart/form-data requests.
 *
 * The function:
 * - Validates the request data against `requestSchema` (if provided)
 * - Creates a FormData instance
 * - Appends all fields, handling File instances, arrays, and other types appropriately
 * - Files are appended with their name property
 * - Arrays of files are appended individually
 * - Other values are serialized using `serializeFormDataValue`
 *
 * @param request - The request parameters object
 * @param options - Endpoint configuration containing request schema
 * @returns A FormData instance ready for multipart requests
 *
 * @example
 * ```ts
 * const formData = makeFormData(
 *   { data: { file: new File(['content'], 'file.txt'), name: 'My File' } },
 *   { requestSchema: z.object({ file: z.instanceof(File), name: z.string() }) }
 * )
 * ```
 *
 * @internal
 */
export function makeFormData<Options extends EndpointOptions | StreamOptions>(
  request: ConfigRequest,
  options: Options,
) {
  const formData = new FormData();
  const validatedRequest = parseWithSchema(
    options.requestSchema as ZodType | undefined,
    request.data,
  ) as Record<string, unknown>;
  if (!validatedRequest || typeof validatedRequest !== 'object') {
    return formData;
  }

  for (const key in validatedRequest) {
    const value = validatedRequest[key];
    if (value instanceof File) {
      formData.append(key, value, value.name);
    } else if (Array.isArray(value)) {
      for (const item of value) {
        appendArrayItem(formData, key, item);
      }
    } else {
      appendScalarField(formData, key, value);
    }
  }
  return formData;
}

/**
 * Appends a single array item, matching the original inline behavior: File
 * instances keep their name; everything else is serialized as-is.
 */
function appendArrayItem(formData: FormData, key: string, item: unknown): void {
  if (item instanceof File) {
    formData.append(key, item, item.name);
  } else {
    formData.append(key, serializeFormDataValue(item));
  }
}

/**
 * Appends a non-array, non-File field value. Serialization may itself yield a
 * File (e.g. a Blob wrapper), in which case its name is preserved.
 */
function appendScalarField(
  formData: FormData,
  key: string,
  value: unknown,
): void {
  const serialized = serializeFormDataValue(value);
  if (serialized instanceof File) {
    formData.append(key, serialized, serialized.name);
  } else {
    formData.append(key, serialized);
  }
}
